// Eskom PARCC — Reports (the Wednesday Composer).
//
// Lindiwe's quarterly + cumulative reporting (10 Sep 2026): the page owns the
// {fy, throughQuarter} scope + the composer state (narrative edits, RTM status),
// and every figure comes from the pure buildQuarterlyReportModel — one pull yields
// a cumulative detailed section plus one high-level slide per quarter, all
// self-consistent by construction. The V4 weekly deck (PPTX) + PDF twin are
// generated from the same model; a Legacy button keeps the old analytics deck for
// one round. Persistence (edited narratives / RTM status as durable rows) lands in
// Phase 1b — here they are component state, frozen into the ReportRun on generate.
import { Fragment, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  FilterBar,
  SelectFilter,
  Switch,
  Banner,
  Button,
  DataTable,
  LoadingState,
  ErrorState,
  EmptyState,
  KpiCard,
  KpiGrid,
  useToast,
  type Column,
} from '../../components/ui';
import { useAuth } from '../../lib/auth';
import { auth } from '../../lib/firebase';
import { adminApi } from '../../lib/adminApi';
import { uploadReportObject } from '../../lib/storage';
import { scopeTransactions, canAuthorRtm } from '../../lib/permissions';
import {
  useTransactions,
  useFindings,
  invalidateReportRuns,
  useReportNarratives,
  useRtmStatus,
  useUpsertReportNarrative,
  useDeleteReportNarrative,
  useUpsertRtmStatus,
} from '../../hooks';
import { insertReportRun } from '../../lib/dbWrite';
import { buildDivisionalFindingsSummary } from '../findings/divisionalSummary';
import { genuineFindings } from '../../domain/findings';
import { effectiveComplianceByRef } from '../transactions/compliance';
import { workbookFromSheet, downloadWorkbook } from '../exports';
import type { Transaction } from '../../data/types';
import { todayIsoSast, type Quarter, QUARTERS } from '../../domain/fiscal';
import {
  buildQuarterlyReportModel,
  type QuarterlyReportModel,
  type NarrativeInput,
  type RtmStatusRow,
} from './buildQuarterlyReportModel';
import {
  defaultScope,
  financialYearsPresent,
  DECK_DIVISION_ORDER,
  deckDivisionLabel,
  type ReportScope,
} from './reportScope';
import { narrativeKey, type SlideKey } from './narratives';
import {
  DIVISIONAL_V4_COLUMNS,
  SOURCE_V4_COLUMNS,
  GAPS_SD_V4_COLUMNS,
  KPI_CARDS_V4,
  type SummaryRow,
  type GapBreakdownRow,
  type V4ColumnDef,
  type V4GapColumnDef,
} from './deckSections';
import { canonicalDivision } from '../../domain/divisions';
import { withLevels } from './deckSections';
import { ReportRegisterPanel, DivisionalReportArchive } from './ReportRegisterPanel';

const PPTX_MIME = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
const PDF_MIME = 'application/pdf';

/** Format an ISO yyyy-mm-dd to "01 June 2026" (en-GB, monolith parity). */
function dateLongFrom(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
}
function filenameStem(model: QuarterlyReportModel): string {
  const span = model.meta.throughQuarter === 'Q1' ? 'Q1' : `Q1-${model.meta.throughQuarter}`;
  return `Audit Recovery Program - ${model.meta.fy} ${span} - ${model.meta.dateLong}`.replace(
    /[\\/:*?"<>|]+/g,
    ' ',
  ).trim();
}

const ALL_DIVISIONS = { value: '', label: 'All divisions' };
const QUARTER_OPTIONS = QUARTERS.map((q) => ({ value: q, label: q }));

export function ReportsPage(): React.JSX.Element {
  const { grant } = useAuth();
  const isAdmin = grant?.role === 'admin';
  const isTeamLead = grant?.role === 'teamLead';
  const canRunReports = isAdmin || grant?.role === 'manager' || isTeamLead;
  const toast = useToast();
  const navigate = useNavigate();
  const transactionsQuery = useTransactions();
  const findingsQuery = useFindings();

  const [scopeOverride, setScopeOverride] = useState<Partial<ReportScope>>({});
  const [busy, setBusy] = useState<null | 'deck' | 'legacy' | 'email' | 'rtm'>(null);
  const [recipient, setRecipient] = useState('');
  // Composer state (Phase 1a — durable rows land in 1b; frozen into the ReportRun here).
  const [narrativeEdits, setNarrativeEdits] = useState<Record<string, string>>({});
  const [rtmEdits, setRtmEdits] = useState<Record<string, { submitted: string; signedOff: string }>>({});

  const transactions = useMemo(
    () => (transactionsQuery.data ?? []).filter((t) => !t.deleted),
    [transactionsQuery.data],
  );
  const findings = useMemo(
    () => (findingsQuery.data ?? []).filter((f) => !f.deleted),
    [findingsQuery.data],
  );
  const visible = useMemo(() => scopeTransactions(transactions, grant), [transactions, grant]);

  // Scope = data-derived default, overlaid with the user's choices. A team lead is
  // locked to their own division.
  const scope: ReportScope = useMemo(() => {
    const base = defaultScope(visible, todayIsoSast());
    const merged = { ...base, ...scopeOverride };
    if (isTeamLead) merged.division = canonicalDivision(grant?.division ?? '');
    return merged;
  }, [visible, scopeOverride, isTeamLead, grant?.division]);

  const fyOptions = useMemo(() => {
    const present = financialYearsPresent(visible);
    const set = new Set(present);
    set.add(scope.fy);
    return [...set].sort((a, b) => Number(b.slice(2)) - Number(a.slice(2))).map((fy) => ({ value: fy, label: fy }));
  }, [visible, scope.fy]);

  const divisionOptions = useMemo(() => {
    const seen = new Set<string>();
    for (const t of visible) {
      const d = canonicalDivision(t.division);
      if (d) seen.add(d);
    }
    return [ALL_DIVISIONS, ...[...seen].sort((a, b) => a.localeCompare(b)).map((d) => ({ value: d, label: deckDivisionLabel(d) }))];
  }, [visible]);

  const effLevel = useMemo(() => effectiveComplianceByRef(visible, findings), [visible, findings]);

  // Persisted composer content for the active FY (admin-only queries; idle for others).
  const persistedNarrativesQuery = useReportNarratives(isAdmin ? scope.fy : undefined);
  const persistedRtmQuery = useRtmStatus(isAdmin ? scope.fy : undefined);
  const upsertNarrative = useUpsertReportNarrative();
  const resetNarrative = useDeleteReportNarrative();
  const upsertRtm = useUpsertRtmStatus();

  // Model inputs = persisted rows, overridden by any unsaved edits in this session.
  const narrativeInputs = useMemo<NarrativeInput[]>(() => {
    const byKey = new Map<string, NarrativeInput>();
    for (const r of persistedNarrativesQuery.data ?? []) {
      byKey.set(r.id, { key: r.id, text: r.text, edited: r.edited, draftInputs: r.draftInputs });
    }
    for (const [key, text] of Object.entries(narrativeEdits)) {
      const prev = byKey.get(key);
      byKey.set(key, { key, text, edited: true, draftInputs: prev?.draftInputs ?? null });
    }
    return [...byKey.values()];
  }, [persistedNarrativesQuery.data, narrativeEdits]);

  const rtmInputs = useMemo<RtmStatusRow[]>(() => {
    const byKey = new Map<string, RtmStatusRow>();
    for (const r of persistedRtmQuery.data ?? []) {
      byKey.set(`${r.quarter}|${r.division}`, { fy: r.fy, quarter: r.quarter, division: r.division, submitted: r.submitted ?? '', signedOff: r.signedOff ?? '', note: r.note ?? '' });
    }
    for (const [k, v] of Object.entries(rtmEdits)) {
      const [quarter, division] = k.split('|');
      byKey.set(k, { fy: scope.fy, quarter, division, submitted: v.submitted, signedOff: v.signedOff });
    }
    return [...byKey.values()];
  }, [persistedRtmQuery.data, rtmEdits, scope.fy]);

  const model = useMemo<QuarterlyReportModel>(
    () =>
      buildQuarterlyReportModel({
        transactions: visible,
        findings,
        scope,
        meta: { dateLong: dateLongFrom(scope.asOfDate), generatedAtIso: '1970-01-01T00:00:00.000Z' },
        narratives: narrativeInputs,
        rtmStatus: rtmInputs,
        effectiveLevelByRef: effLevel,
      }),
    [visible, findings, scope, narrativeInputs, rtmInputs, effLevel],
  );

  // --- Composer persistence (Phase 1b) ---------------------------------------
  function parseNarrativeKey(key: string): { fy: string; scopeKey: string; slideKey: string; division: string } {
    const [fy, scopeKey, slideKey, division] = key.split('|');
    return { fy, scopeKey, slideKey, division: division ?? '' };
  }
  async function saveNarrative(key: string): Promise<void> {
    const text = narrativeEdits[key];
    if (text == null) return;
    const parts = parseNarrativeKey(key);
    try {
      await upsertNarrative.mutateAsync({
        input: { id: key, ...parts, text, draftInputs: model.fingerprints[key] ?? null, edited: true },
        note: `Edited ${parts.slideKey}${parts.division ? ` (${parts.division})` : ''}`,
      });
      setNarrativeEdits((m) => { const n = { ...m }; delete n[key]; return n; });
      toast.show('Narrative saved.', 'success');
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'Could not save the narrative.', 'error');
    }
  }
  async function resetNarrativeToAuto(key: string): Promise<void> {
    try {
      await resetNarrative.mutateAsync(key);
      setNarrativeEdits((m) => { const n = { ...m }; delete n[key]; return n; });
      toast.show('Narrative reset to the auto-draft.', 'success');
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'Could not reset the narrative.', 'error');
    }
  }
  async function saveRtmStatus(): Promise<void> {
    const entries = Object.entries(rtmEdits);
    if (entries.length === 0) { toast.show('No RTM status changes to save.', 'info'); return; }
    try {
      for (const [k, v] of entries) {
        const [quarter, division] = k.split('|');
        await upsertRtm.mutateAsync({
          input: { id: `${scope.fy}|${quarter}|${division}`, fy: scope.fy, quarter, division, submitted: v.submitted, signedOff: v.signedOff },
          note: `RTM ${quarter} ${division}`,
        });
      }
      setRtmEdits({});
      toast.show('Reports-to-management status saved.', 'success');
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'Could not save the RTM status.', 'error');
    }
  }

  /** Download an auto-drafted Report to Management (DOCX + PDF) for the chosen division. */
  async function downloadRtm(): Promise<void> {
    if (!canRunReports) return;
    const division = scope.division;
    if (!division) {
      toast.show('Choose a division to draft its Report to Management.', 'info');
      return;
    }
    setBusy('rtm');
    try {
      const divRows = visible.filter((t) => !t.deleted && canonicalDivision(t.division) === division);
      const based = withLevels(divRows, (t) => effLevel.get(t.referenceNo) ?? t.complianceLevel ?? '');
      const [{ buildRtmDocModel }, { saveBlob, generateRtmDocxBlob, generateRtmPdfBlob }] = await Promise.all([
        import('./rtm/buildRtmDocModel'),
        import('./rtm/download'),
      ]);
      const rtmModel = buildRtmDocModel({
        fy: scope.fy,
        quarter: scope.throughQuarter,
        division,
        longName: deckDivisionLabel(division),
        reportDate: dateLongFrom(scope.asOfDate),
        rows: based,
        findings,
      });
      const stem = `Report to Management - ${deckDivisionLabel(division)} - ${scope.fy} ${scope.throughQuarter}`.replace(/[\\/:*?"<>|]+/g, ' ').trim();
      const [docx, pdf] = await Promise.all([generateRtmDocxBlob(rtmModel), generateRtmPdfBlob(rtmModel)]);
      saveBlob(docx, `${stem}.docx`);
      saveBlob(pdf, `${stem}.pdf`);
      toast.show('Report to Management (draft) downloaded — edit in Word, then issue.', 'success');
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast.show(`Could not generate the Report to Management: ${message}`, 'error');
    } finally {
      setBusy(null);
    }
  }

  const isLoading = transactionsQuery.isLoading || findingsQuery.isLoading;
  const error = transactionsQuery.error ?? findingsQuery.error;

  /** Rebuild the model with a fresh generation timestamp at click time. */
  function builtModel(): QuarterlyReportModel {
    return buildQuarterlyReportModel({
      transactions: visible,
      findings,
      scope,
      meta: { dateLong: dateLongFrom(scope.asOfDate), generatedAtIso: new Date().toISOString() },
      narratives: narrativeInputs,
      rtmStatus: rtmInputs,
      effectiveLevelByRef: effLevel,
    });
  }

  /** Generate the V4 weekly deck (PPTX + PDF), save + persist + record ONE ReportRun. */
  async function generateDeck(): Promise<void> {
    if (!canRunReports) {
      toast.show('Reports are restricted to managers and administrators.', 'error');
      return;
    }
    const staleCount = Object.values(model.narratives).filter((r) => r.stale).length;
    if (staleCount > 0 && !window.confirm(`${staleCount} narrative(s) are stale (their numbers moved). Generate anyway?`)) return;
    setBusy('deck');
    try {
      const built = builtModel();
      const stem = filenameStem(built);
      const { saveBlob, generateWeeklyDeckBlob, generateWeeklyPdfBlob } = await import('./download');
      const [pptx, pdf] = await Promise.all([generateWeeklyDeckBlob(built), generateWeeklyPdfBlob(built)]);
      saveBlob(pptx, `${stem}.pptx`);
      saveBlob(pdf, `${stem}.pdf`);
      toast.show('Weekly deck (PPTX + PDF) downloaded.', 'success');

      // Best-effort retention + audit, AFTER the download.
      const uid = auth.currentUser?.uid;
      const stampDivision = scope.division || '';
      const storagePaths: Record<string, string> = {};
      try {
        if (uid) {
          storagePaths.pptx = await uploadReportObject(uid, `${stem}.pptx`, pptx, PPTX_MIME);
          storagePaths.pdf = await uploadReportObject(uid, `${stem}.pdf`, pdf, PDF_MIME);
          const modelBlob = new Blob([JSON.stringify(built)], { type: 'application/json' });
          storagePaths.model = await uploadReportObject(uid, `${stem}.model.json`, modelBlob, 'application/json');
        }
      } catch (storageError) {
        console.warn('Report persist failed (non-blocking):', storageError);
        toast.show('Downloaded, but the archive copy could not be stored.', 'info');
      }
      let reportNumber: string | undefined;
      try {
        const r = await adminApi.reportsNextNumber(scope.asOfDate, stampDivision || undefined, 'deck');
        reportNumber = r.reportNumber;
      } catch (numberError) {
        console.warn('Report numbering failed (non-blocking):', numberError);
      }
      try {
        const dTot = built.cumulative.divisional.find((r) => r.key === 'Total');
        await insertReportRun({
          type: 'weekly_deck',
          format: 'pptx+pdf',
          storagePath: storagePaths.pptx,
          reportNumber,
          asOfDate: scope.asOfDate,
          division: stampDivision || undefined,
          parameters: {
            fy: scope.fy,
            throughQuarter: scope.throughQuarter,
            asOfDate: scope.asOfDate,
            division: stampDivision,
            appendix: scope.appendix,
            template: 'v4',
            formats: ['pptx', 'pdf'],
          },
          summary: {
            totals: dTot,
            quarters: built.quarters.map((q) => ({ quarter: q.quarter, kpis: q.kpis })),
            narratives: built.narratives,
            rtmStatus: built.rtmStatus,
            invariants: built.invariants,
            storagePaths,
            reportNumber,
          },
        });
        invalidateReportRuns();
      } catch (auditError) {
        console.warn('Report-run audit failed (non-blocking):', auditError);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast.show(`Could not generate the deck: ${message}`, 'error');
    } finally {
      setBusy(null);
    }
  }

  /** Legacy analytics deck (the pre-V4 buildPptx) — kept for one round as a fallback. */
  async function generateLegacy(): Promise<void> {
    if (!canRunReports) return;
    setBusy('legacy');
    try {
      const built = builtModel();
      if (!built.appendix) {
        // The Legacy deck is the analytics model; build it via the appendix path.
        const withAppendix = buildQuarterlyReportModel({
          transactions: visible,
          findings,
          scope: { ...scope, appendix: true },
          meta: { dateLong: dateLongFrom(scope.asOfDate), generatedAtIso: new Date().toISOString() },
          narratives: narrativeInputs,
          rtmStatus: rtmInputs,
          effectiveLevelByRef: effLevel,
        });
        const { saveBlob, generatePptxBlob } = await import('./download');
        const blob = await generatePptxBlob(withAppendix.appendix!);
        saveBlob(blob, `${filenameStem(built)} (legacy).pptx`);
        toast.show('Legacy analytics deck downloaded.', 'success');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast.show(`Could not generate the legacy deck: ${message}`, 'error');
    } finally {
      setBusy(null);
    }
  }

  /** Email the deck + PDF as attachments via the admin-api (admin only). */
  async function emailReport(): Promise<void> {
    if (!isAdmin) {
      toast.show('Emailing a report is restricted to administrators.', 'error');
      return;
    }
    const to = recipient.trim();
    if (!to || !to.includes('@')) {
      toast.show('Enter a valid recipient email.', 'error');
      return;
    }
    setBusy('email');
    try {
      const built = builtModel();
      const stem = filenameStem(built);
      const { generateWeeklyDeckBase64, generateWeeklyPdfBase64 } = await import('./download');
      const [pptx, pdf] = await Promise.all([generateWeeklyDeckBase64(built), generateWeeklyPdfBase64(built)]);
      const span = built.meta.throughQuarter === 'Q1' ? 'Q1' : `Q1 – ${built.meta.throughQuarter}`;
      await adminApi.emailReport({
        recipientEmail: to,
        periodTitle: `${built.meta.fy} ${span}`,
        scopeLabel: built.meta.division || built.meta.rangeLabel,
        attachments: [
          { filename: `${stem}.pdf`, mime: PDF_MIME, b64: pdf },
          { filename: `${stem}.pptx`, mime: PPTX_MIME, b64: pptx },
        ],
      });
      toast.show(`Report emailed to ${to}.`, 'success');
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast.show(`Could not email the report: ${message}`, 'error');
    } finally {
      setBusy(null);
    }
  }

  /** Review summary by division (all in-scope findings) as XLSX. */
  function downloadReviewSummary(): void {
    if (!canRunReports) return;
    try {
      const refs = new Set<string>();
      const txByRef = new Map<string, Transaction>();
      for (const t of visible) {
        if (scope.division && canonicalDivision(t.division) !== scope.division) continue;
        refs.add(t.referenceNo);
        if (t.referenceNo) txByRef.set(t.referenceNo, t);
      }
      const lines = genuineFindings(findings).filter((f) => f.referenceNo && refs.has(f.referenceNo));
      if (lines.length === 0) {
        toast.show('There are no findings in scope to summarise.', 'info');
        return;
      }
      const sheet = buildDivisionalFindingsSummary(lines, txByRef);
      downloadWorkbook(`review_summary_by_division_${scope.asOfDate}.xlsx`, workbookFromSheet('Review Summary', sheet));
      toast.show(`Exported ${lines.length.toLocaleString('en-ZA')} finding(s), grouped by division.`, 'success');
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast.show(`Could not export the review summary: ${message}`, 'error');
    }
  }

  if (isLoading) return <LoadingState message="Loading report data…" />;
  if (error) {
    return (
      <ErrorState
        message="Could not load the report data."
        detail={error instanceof Error ? error.message : String(error)}
        onRetry={() => {
          void transactionsQuery.refetch();
          void findingsQuery.refetch();
        }}
      />
    );
  }
  if (transactions.length === 0) return <EmptyState message="No transactions have been captured yet." />;

  const cumScopeKey = scope.throughQuarter === 'Q1' ? 'CUM:Q1' : `CUM:${scope.throughQuarter}`;
  const kpiTotal = model.cumulative.kpiCards;
  const span = scope.throughQuarter === 'Q1' ? 'Q1' : `Q1 – ${scope.throughQuarter}`;
  const hasError = model.readiness.some((l) => l.kind === 'error');

  return (
    <>
      <Card
        title="Audit Recovery Programme — weekly deck"
        subtitle={`${scope.fy} · ${span} cumulative · ${kpiTotal.population.toLocaleString('en-ZA')} records${scope.division ? ` · ${deckDivisionLabel(scope.division)}` : ''}`}
        actions={
          <div className="actions">
            <Button variant="primary" onClick={() => void generateDeck()} disabled={busy !== null || hasError}>
              {busy === 'deck' ? 'Generating…' : 'Generate deck (PPTX + PDF)'}
            </Button>
            <Button variant="ghost" onClick={downloadReviewSummary} disabled={busy !== null}>
              Review summary (XLSX)
            </Button>
            <Button variant="ghost" onClick={() => void generateLegacy()} disabled={busy !== null}>
              {busy === 'legacy' ? 'Generating…' : 'Legacy deck'}
            </Button>
            {scope.division && (
              <Button variant="ghost" onClick={() => void downloadRtm()} disabled={busy !== null}>
                {busy === 'rtm' ? 'Drafting…' : 'Report to Management (draft)'}
              </Button>
            )}
            {scope.division && canAuthorRtm(grant, scope.division) && (
              <Button
                variant="ghost"
                onClick={() =>
                  navigate(
                    `/rtm/${encodeURIComponent(scope.fy)}/${encodeURIComponent(scope.throughQuarter)}/${encodeURIComponent(scope.division)}`,
                  )
                }
                disabled={busy !== null}
              >
                Open RTM composer
              </Button>
            )}
            {isAdmin && (
              <Button variant="ghost" onClick={() => void emailReport()} disabled={busy !== null}>
                {busy === 'email' ? 'Emailing…' : 'Email report'}
              </Button>
            )}
          </div>
        }
      >
        <FilterBar>
          <label>
            As of date
            <input type="date" value={scope.asOfDate} onChange={(e) => setScopeOverride((s) => ({ ...s, asOfDate: e.target.value }))} />
          </label>
          <SelectFilter label="Financial year" value={scope.fy} options={fyOptions} onChange={(fy) => setScopeOverride((s) => ({ ...s, fy }))} />
          <SelectFilter label="Through quarter" value={scope.throughQuarter} options={QUARTER_OPTIONS} onChange={(q) => setScopeOverride((s) => ({ ...s, throughQuarter: q as Quarter }))} />
          {!isTeamLead && (
            <SelectFilter label="Division" value={scope.division} options={divisionOptions} onChange={(division) => setScopeOverride((s) => ({ ...s, division }))} />
          )}
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            Analytics appendix
            <Switch checked={scope.appendix} onChange={(appendix) => setScopeOverride((s) => ({ ...s, appendix }))} ariaLabel="Include analytics appendix" />
          </label>
          {isAdmin && (
            <label>
              Email report to
              <input type="email" placeholder="name@kettleconsulting.co.za" value={recipient} onChange={(e) => setRecipient(e.target.value)} autoComplete="off" />
            </label>
          )}
        </FilterBar>
      </Card>

      {/* Data readiness — every row the scope could not place, surfaced not dropped. */}
      {model.readiness.length > 0 && (
        <div style={{ margin: '12px 0' }}>
          <Card title="Data readiness" subtitle="What is in scope, and everything the scope could not place.">
            <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
              {model.readiness.map((line, i) => (
                <li key={i} style={{ color: line.kind === 'error' ? '#b42318' : line.kind === 'warn' ? '#9a6b00' : undefined }}>
                  {line.text}
                </li>
              ))}
            </ul>
          </Card>
        </div>
      )}

      {model.invariants.warnings.length > 0 && (
        <div style={{ margin: '12px 0' }}>
          <Banner tone="bad">
            {model.invariants.warnings.length} self-consistency check(s) failed — the numbers below may not reconcile.
          </Banner>
        </div>
      )}

      <div style={{ margin: '16px 0' }}>
        <KpiGrid>
          {KPI_CARDS_V4.map((c) => (
            <KpiCard key={c.key as string} label={c.label} value={((kpiTotal[c.key] as number) ?? 0).toLocaleString('en-ZA')} />
          ))}
        </KpiGrid>
      </div>

      {/* Narrative editors — the deck's authored prose (component state in 1a). */}
      <div className="dashboard-grid">
        <Card className="span-6" title="Gaps & self-declaration — narrative" subtitle="Slide 2 box.">
          <NarrativeField model={model} scopeKey={cumScopeKey} slideKey="gaps-sd" edits={narrativeEdits} setEdits={setNarrativeEdits} onSave={saveNarrative} onReset={resetNarrativeToAuto} />
        </Card>
        <Card className="span-6" title="Divisional summary — footer" subtitle="Slide 3 footer box.">
          <NarrativeField model={model} scopeKey={cumScopeKey} slideKey="divisional-footer" edits={narrativeEdits} setEdits={setNarrativeEdits} onSave={saveNarrative} onReset={resetNarrativeToAuto} />
        </Card>
      </div>

      {/* Slide previews — the exact V4 tables the deck renders. */}
      <div className="dashboard-grid">
        <Card className="span-12" title="Summary of gaps and self-declaration" subtitle={model.titles.gapsSd}>
          <DataTable columns={v4GapColumns('Operating Unit/Power station', GAPS_SD_V4_COLUMNS)} rows={model.cumulative.gapsSd} getRowKey={(r) => r.key} />
        </Card>
        <Card className="span-12" title="Divisional summary (cumulative)" subtitle={model.titles.divisional}>
          <DataTable columns={v4Columns('Division', DIVISIONAL_V4_COLUMNS)} rows={model.cumulative.divisional} getRowKey={(r) => r.key} />
        </Card>
        <Card className="span-12" title="Summary by source" subtitle={model.titles.bySource}>
          <DataTable columns={v4Columns('Transaction Type', SOURCE_V4_COLUMNS)} rows={model.cumulative.bySource} getRowKey={(r) => r.key} />
        </Card>
        {model.quarters.map((q) => (
          <Card key={q.quarter} className="span-12" title={`${q.quarter} divisional summary`} subtitle={model.titles.quarters[q.quarter]}>
            <DataTable columns={v4Columns('Division', DIVISIONAL_V4_COLUMNS)} rows={q.divisional} getRowKey={(r) => r.key} />
          </Card>
        ))}
      </div>

      {/* Reports-to-management status (slide 7) — free-text cells per quarter. */}
      <div style={{ margin: '12px 0' }}>
        <Card title="Reports to management — status" subtitle={model.titles.reportsToManagement}>
          <RtmStatusGrid quarters={model.meta.quarters} divisions={scope.division ? [scope.division] : DECK_DIVISION_ORDER} edits={rtmEdits} setEdits={setRtmEdits} persisted={rtmInputs} onSave={saveRtmStatus} saving={upsertRtm.isPending} />
        </Card>
      </div>

      {isAdmin ? <ReportRegisterPanel /> : <DivisionalReportArchive division={isTeamLead ? (grant?.division ?? '') : scope.division} />}
    </>
  );
}

// ---- Narrative editor (persisted in Phase 1b) -------------------------------
function NarrativeField(props: {
  model: QuarterlyReportModel;
  scopeKey: string;
  slideKey: SlideKey;
  division?: string;
  edits: Record<string, string>;
  setEdits: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  onSave: (key: string) => void;
  onReset: (key: string) => void;
}): React.JSX.Element {
  const key = narrativeKey(props.model.meta.fy, props.scopeKey, props.slideKey, props.division ?? '');
  const resolved = props.model.narratives[key];
  const dirty = key in props.edits;
  const value = props.edits[key] ?? resolved?.text ?? '';
  const persistedEdited = resolved?.source === 'edited';
  return (
    <div>
      <textarea
        value={value}
        rows={4}
        style={{ width: '100%', fontFamily: 'inherit', fontSize: 13 }}
        onChange={(e) => props.setEdits((m) => ({ ...m, [key]: e.target.value }))}
      />
      {resolved?.stale && !dirty && (
        <div className="small" style={{ color: '#9a6b00', marginTop: 4 }}>
          The numbers moved since this was written — review, then Save to re-stamp or Reset to re-draft.
        </div>
      )}
      <div className="small" style={{ marginTop: 4, display: 'flex', gap: 8, alignItems: 'center' }}>
        <span style={{ color: dirty ? '#9a6b00' : persistedEdited ? '#5b6b7b' : '#5b6b7b' }}>
          {dirty ? 'Unsaved edit' : persistedEdited ? 'Edited' : 'Auto-drafted from the numbers'}
        </span>
        {dirty && <Button variant="primary" onClick={() => props.onSave(key)}>Save</Button>}
        {(dirty || persistedEdited) && (
          <Button variant="ghost" onClick={() => props.onReset(key)}>Reset to auto</Button>
        )}
      </div>
    </div>
  );
}

// ---- Reports-to-management status grid (persisted in Phase 1b) -------------
function RtmStatusGrid(props: {
  quarters: readonly Quarter[];
  divisions: readonly string[];
  edits: Record<string, { submitted: string; signedOff: string }>;
  setEdits: React.Dispatch<React.SetStateAction<Record<string, { submitted: string; signedOff: string }>>>;
  persisted: readonly RtmStatusRow[];
  onSave: () => void;
  saving: boolean;
}): React.JSX.Element {
  const persistedByKey = new Map(props.persisted.map((r) => [`${r.quarter}|${r.division}`, r]));
  const set = (q: string, d: string, field: 'submitted' | 'signedOff', v: string): void => {
    const k = `${q}|${d}`;
    props.setEdits((m) => {
      const persistedRow = persistedByKey.get(k);
      const prev = m[k] ?? { submitted: persistedRow?.submitted ?? '', signedOff: persistedRow?.signedOff ?? '' };
      return { ...m, [k]: { ...prev, [field]: v } };
    });
  };
  const cell = (q: string, d: string, field: 'submitted' | 'signedOff'): string => {
    const k = `${q}|${d}`;
    if (props.edits[k]) return props.edits[k][field];
    return (persistedByKey.get(k)?.[field] as string) ?? '';
  };
  const dirty = Object.keys(props.edits).length > 0;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="dz-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left' }}>Operating Unit</th>
            {props.quarters.map((q) => (
              <th key={q} colSpan={2} style={{ textAlign: 'center' }}>{q}</th>
            ))}
          </tr>
          <tr>
            <th />
            {props.quarters.map((q) => (
              <Fragment key={q}>
                <th className="small">RTM Submitted</th>
                <th className="small">RTM signed off</th>
              </Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {props.divisions.map((d) => (
            <tr key={d}>
              <td>{deckDivisionLabel(d)}</td>
              {props.quarters.map((q) => (
                <Fragment key={q}>
                  <td><input value={cell(q, d, 'submitted')} placeholder="—" style={{ width: 90 }} onChange={(e) => set(q, d, 'submitted', e.target.value)} /></td>
                  <td><input value={cell(q, d, 'signedOff')} placeholder="—" style={{ width: 110 }} onChange={(e) => set(q, d, 'signedOff', e.target.value)} /></td>
                </Fragment>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: 10 }}>
        <Button variant="primary" onClick={props.onSave} disabled={!dirty || props.saving}>
          {props.saving ? 'Saving…' : 'Save RTM status'}
        </Button>
      </div>
    </div>
  );
}

// ---- Preview column adapters (V4 registries → DataTable) --------------------
function v4Columns(keyHeader: string, defs: readonly V4ColumnDef[]): Column<SummaryRow>[] {
  return [
    { key: 'key', header: keyHeader, sortable: true, render: (r) => deckDivisionLabel(r.key) },
    ...defs.map((c) => ({ key: c.key, header: c.header, align: 'right' as const, render: (r: SummaryRow) => c.value(r) })),
  ];
}
function v4GapColumns(keyHeader: string, defs: readonly V4GapColumnDef[]): Column<GapBreakdownRow>[] {
  return [
    { key: 'key', header: keyHeader, sortable: true, render: (r) => deckDivisionLabel(r.key) },
    ...defs.map((c) => ({
      key: c.key,
      header: (c as { group?: string }).group ? `${(c as { group?: string }).group} · ${c.header}` : c.header,
      align: 'right' as const,
      render: (r: GapBreakdownRow) => c.value(r),
    })),
  ];
}
